import { useState, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Upload, FileWarning, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "@/i18n/use-translation";
import { apiError, showApiErrorAs } from "@/lib/api-error";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

interface PreviewRow {
  row: number;
  valid: boolean;
  data: Record<string, string | null>;
  errors: string[];
}

interface PreviewResult {
  total_rows: number;
  valid_count: number;
  error_count: number;
  rows: PreviewRow[];
}

export function ImportSitesDialog() {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);

  const previewMutation = useMutation({
    mutationFn: async (f: File) => {
      const form = new FormData();
      form.append("file", f);
      const h: Record<string, string> = {};
      for (const [k, v] of Object.entries(headers)) {
        if (k !== "Content-Type") h[k] = v;
      }
      const res = await fetch(`${API_URL}/api/v1/sites/import?preview=true`, {
        method: "POST",
        headers: h,
        body: form,
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      return (await res.json()).data as PreviewResult;
    },
    onSuccess: (data) => setPreview(data),
    onError: (err: Error) => showApiErrorAs(t("settings.importPreviewFailed"), err),
  });

  const commitMutation = useMutation({
    mutationFn: async (f: File) => {
      const form = new FormData();
      form.append("file", f);
      const h: Record<string, string> = {};
      for (const [k, v] of Object.entries(headers)) {
        if (k !== "Content-Type") h[k] = v;
      }
      const res = await fetch(`${API_URL}/api/v1/sites/import`, {
        method: "POST",
        headers: h,
        body: form,
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      return (await res.json()).data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["sites"] });
      toast.success(t("settings.sitesImported", { count: data.success_count }));
      setOpen(false);
      setFile(null);
      setPreview(null);
    },
    onError: (err: Error) => showApiErrorAs(t("settings.importFailed"), err),
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
    previewMutation.mutate(f);
  }

  async function handleDownloadTemplate() {
    try {
      const res = await fetch(`${API_URL}/api/v1/sites/import/template`, { headers });
      if (!res.ok) throw new Error("Failed to download template");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sites-import-template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("settings.templateDownloadFailed"));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) {
          setFile(null);
          setPreview(null);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <Upload className="mr-1 size-4" />
          {t("settings.importButton")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("settings.importSitesFromCsv")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Button size="sm" variant="outline" onClick={handleDownloadTemplate}>
              {t("settings.downloadTemplateButton")}
            </Button>
            <div className="relative">
              <input
                ref={fileRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="absolute inset-0 cursor-pointer opacity-0"
              />
              <Button size="sm" variant="outline">
                {file ? file.name : t("settings.chooseCsvFile")}
              </Button>
            </div>
          </div>

          {previewMutation.isPending && (
            <p className="text-sm text-muted-foreground">{t("settings.validating")}</p>
          )}

          {preview && (
            <>
              <div className="flex items-center gap-4 text-sm">
                <span>{t("settings.rowsCount", { count: preview.total_rows })}</span>
                <span className="flex items-center gap-1 text-emerald-700">
                  <CheckCircle2 className="size-3.5" />{" "}
                  {t("settings.validCount", { count: preview.valid_count })}
                </span>
                {preview.error_count > 0 && (
                  <span className="flex items-center gap-1 text-red-600">
                    <XCircle className="size-3.5" />{" "}
                    {t("settings.errorCount", { count: preview.error_count })}
                  </span>
                )}
              </div>

              <div className="max-h-64 overflow-auto rounded border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">{t("settings.columnRow")}</TableHead>
                      <TableHead>{t("settings.columnAccount")}</TableHead>
                      <TableHead>{t("settings.columnName")}</TableHead>
                      <TableHead>{t("settings.columnType")}</TableHead>
                      <TableHead className="w-16">{t("settings.columnStatus")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.rows.map((r) => (
                      <TableRow key={r.row}>
                        <TableCell className="text-xs text-muted-foreground">{r.row}</TableCell>
                        <TableCell className="text-sm">{r.data.account_name}</TableCell>
                        <TableCell className="text-sm">{r.data.name}</TableCell>
                        <TableCell className="text-sm">{r.data.site_type}</TableCell>
                        <TableCell>
                          {r.valid ? (
                            <Badge className="border-transparent bg-emerald-50 text-emerald-700">
                              OK
                            </Badge>
                          ) : (
                            <Badge
                              className="border-transparent bg-red-50 text-red-700"
                              title={r.errors.join(", ")}
                            >
                              <FileWarning className="mr-1 size-3" />
                              {t("settings.errorBadge")}
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {preview.valid_count > 0 && (
                <Button
                  onClick={() => file && commitMutation.mutate(file)}
                  disabled={commitMutation.isPending}
                  className="w-full"
                >
                  {commitMutation.isPending
                    ? t("settings.importing")
                    : t("settings.importSites", { count: preview.valid_count })}
                </Button>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
